import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Users, RefreshCw, Download, Search,
  ChevronLeft, ChevronRight, CheckCircle, AlertCircle,
  X, Clock, Camera, MapPin, Shield,
  MoreVertical, Edit2, TrendingUp, IndianRupee,
  Check, XCircle, Calendar, ChevronDown, ChevronUp,
  ZoomIn,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

const API = API_BASE_URL;

// ── Attendance config
const ATT_CONFIG = {
  FD: { label: "Present", badge: "FD", tw: "bg-[rgba(16,185,129,0.12)] text-[#10b981] border-[rgba(16,185,129,0.28)]", selected: "bg-[rgba(16,185,129,0.2)] text-[#10b981] border-[#10b981]" },
  FN: { label: "Present", badge: "FN", tw: "bg-[rgba(99,102,241,0.12)] text-[#6366f1] border-[rgba(99,102,241,0.28)]", selected: "bg-[rgba(99,102,241,0.2)] text-[#6366f1] border-[#6366f1]" },
  HD: { label: "Half Day", badge: "HD", tw: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b] border-[rgba(245,158,11,0.28)]", selected: "bg-[rgba(245,158,11,0.2)] text-[#f59e0b] border-[#f59e0b]" },
  HN: { label: "Half Day", badge: "HN", tw: "bg-[rgba(251,146,60,0.12)] text-[#fb923c] border-[rgba(251,146,60,0.28)]", selected: "bg-[rgba(251,146,60,0.2)] text-[#fb923c] border-[#fb923c]" },
  DD: { label: "Present", badge: "DD", tw: "bg-[rgba(59,130,246,0.12)] text-[#3b82f6] border-[rgba(59,130,246,0.28)]", selected: "bg-[rgba(59,130,246,0.2)] text-[#3b82f6] border-[#3b82f6]" },
  A: { label: "Absent", badge: "A", tw: "bg-[rgba(239,68,68,0.12)] text-[#ef4444] border-[rgba(239,68,68,0.28)]", selected: "bg-[rgba(239,68,68,0.2)] text-[#ef4444] border-[#ef4444]" },
  "—": { label: "Pending", badge: "—", tw: "bg-[var(--surface)] text-[var(--text-muted)] border-[var(--surface-border)]", selected: "bg-[var(--surface-border)] text-[var(--text-primary)] border-[var(--text-muted)]" },
};

const ATT_OPTIONS = [
  { value: "FD", label: "Full Day (FD)" },
  { value: "FN", label: "Full Night (FN)" },
  { value: "HD", label: "Half Day (HD)" },
  { value: "HN", label: "Half Night (HN)" },
  { value: "DD", label: "Double Duty (DD)" },
  { value: "A", label: "Absent (A)" },
];

const REASON_PRESETS = [
  "Guard was on field duty outside campus",
  "Network/device issue prevented selfie upload",
  "Emergency deployment to another site",
  "Medical leave — documents submitted",
  "Admin verified presence via phone",
  "Casual leave approved",
  "Duty extended from previous shift",
  "Other (custom reason)",
];

const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fmtTime = (d) => new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
const fmtCheckTime = (raw) => {
  if (!raw) return "—";
  const s = String(raw);
  const iso = s.includes("T") ? (s.endsWith("Z") ? s : s + "Z") : s.replace(" ", "T") + "Z";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "UTC" });
};

const fmtSelfieTime = (raw) => {
  if (!raw) return "—";
  const s = String(raw);
  const iso = s.includes("T") ? (s.endsWith("Z") ? s : s + "Z") : s.replace(" ", "T") + "Z";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
  });
};

const getToday = () => new Date().toISOString().split("T")[0];

// ─────────────────────────────────────────────
// Attendance Badge
// ─────────────────────────────────────────────
const AttBadge = ({ desc }) => {
  const cfg = ATT_CONFIG[desc] || ATT_CONFIG["—"];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold border ${cfg.tw}`} style={{ fontFamily: FONT_UI }}>
      {cfg.label}
      <span className="font-mono text-[9px] opacity-80">({cfg.badge})</span>
    </span>
  );
};

// ─────────────────────────────────────────────
// Source Badge
// ─────────────────────────────────────────────
const SourceBadge = ({ source }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-extrabold border ${
      source === "Automatic" 
        ? "bg-[rgba(99,102,241,0.12)] text-[#6366f1] border-[rgba(99,102,241,0.28)]" 
        : "bg-[rgba(251,146,60,0.12)] text-[#fb923c] border-[rgba(251,146,60,0.28)]"
    }`}
    style={{ fontFamily: FONT_UI }}
  >
    {source === "Automatic" ? "⚙ Auto" : source === "Manual" ? "✏ Manual" : "—"}
  </span>
);

// ─────────────────────────────────────────────
// Selfie Count Summary Cell
// ─────────────────────────────────────────────
const SelfieCountCell = ({ row, onOpenViewer }) => {
  const uploaded = row.verified_selfies || 0;
  const pending = row.pending_selfies || 0;
  const inCampus = row.total_scheduled || 0;
  const notUploaded = row.not_uploaded || 0;

  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={(e) => { e.stopPropagation(); onOpenViewer(row); }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border whitespace-nowrap w-fit ${
          uploaded > 0 
            ? "bg-[rgba(16,185,129,0.12)] text-[#10b981] border-[rgba(16,185,129,0.28)]" 
            : "bg-[var(--surface)] text-[var(--text-muted)] border-[var(--surface-border)]"
        }`}
        style={{ fontFamily: FONT_UI }}
      >
        <Camera size={10} /> {uploaded} uploaded
      </button>

      <div
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border whitespace-nowrap w-fit ${
          inCampus > 0 
            ? "bg-[rgba(59,130,246,0.12)] text-[#3b82f6] border-[rgba(59,130,246,0.28)]" 
            : "bg-[var(--surface)] text-[var(--text-muted)] border-[var(--surface-border)]"
        }`}
        style={{ fontFamily: FONT_UI }}
      >
        <MapPin size={10} /> {inCampus} in-campus
      </div>

      {notUploaded > 0 && (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border bg-[var(--red-faint)] text-[var(--red)] border-[var(--red-border)] whitespace-nowrap w-fit" style={{ fontFamily: FONT_UI }}>
          <XCircle size={10} /> {notUploaded} not uploaded
        </div>
      )}

      {pending > 0 && (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border bg-[rgba(245,158,11,0.12)] text-[#f59e0b] border-[rgba(245,158,11,0.28)] whitespace-nowrap w-fit" style={{ fontFamily: FONT_UI }}>
          <Clock size={10} /> {pending} pending
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Expandable Row
// ─────────────────────────────────────────────
const ExpandedRow = ({ row, colSpan, onOpenViewer }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    fetch(`${API}/sentinel-attendance/guard-detail?member_id=${row.member_id}&date=${row.att_date}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setDetail(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [row.member_id, row.att_date]);

  const campusChecks = detail?.campus_checks || [];
  const selfies = detail?.selfies || [];

  const statusClasses = (s) => {
    if (s === "completed" || s === "verified") return { dot: "bg-emerald-500", pill: "bg-[rgba(16,185,129,0.12)] text-[#10b981]" };
    if (s === "notified") return { dot: "bg-amber-500", pill: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]" };
    return { dot: "bg-red-500", pill: "bg-[rgba(239,68,68,0.12)] text-[#ef4444]" };
  };

  return (
    <tr>
      <td colSpan={colSpan} className="p-0 border-b-2 border-[var(--accent)]">
        <div className="bg-[var(--accent-faint)] px-6 py-4">
          {loading ? (
            <div className="flex items-center gap-2 text-[var(--text-muted)] text-[13px] py-2" style={{ fontFamily: FONT_UI }}>
              <div className="w-3.5 h-3.5 border-2 border-[var(--surface-border)] border-t-[var(--accent)] rounded-full animate-spin" />
              Loading details…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: Campus Check Timeline */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={13} className="text-[var(--accent)]" />
                  <span className="text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide" style={{ fontFamily: FONT_UI }}>
                    In-Campus Checks ({campusChecks.length})
                  </span>
                  {campusChecks.length > 0 && (
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-[rgba(16,185,129,0.12)] text-[#10b981] text-[10px] font-extrabold" style={{ fontFamily: FONT_UI }}>
                      {campusChecks.filter(c => c.status === "completed" || c.status === "verified").length}/{campusChecks.length} done
                    </span>
                  )}
                </div>

                {campusChecks.length === 0 ? (
                  <div className="px-4 py-3 bg-[var(--card-bg)] rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] text-center" style={{ fontFamily: FONT_UI }}>
                    No campus checks scheduled for this date
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {campusChecks.map((c, i) => {
                      const isDone = c.status === "completed" || c.status === "verified";
                      const sc = statusClasses(c.status);
                      return (
                        <div key={c.id} className={`flex items-start gap-3 px-3 py-2.5 bg-[var(--card-bg)] rounded-xl border ${isDone ? "border-[rgba(16,185,129,0.28)]" : "border-[var(--surface-border)]"}`}>
                          <div className="flex flex-col items-center pt-0.5">
                            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sc.dot}`} />
                            {i < campusChecks.length - 1 && <div className="w-px flex-1 min-h-[12px] bg-[var(--divider)] mt-1" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[var(--text-primary)] font-mono text-[13px] font-extrabold">{fmtCheckTime(c.check_time)}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase ${sc.pill}`}>{c.status}</span>
                            </div>
                            {c.remarks && <div className="text-[var(--text-muted)] text-[11px] mt-0.5" style={{ fontFamily: FONT_UI }}>{c.remarks}</div>}
                            {c.checked_at && <div className="text-[#10b981] text-[10px] mt-0.5" style={{ fontFamily: FONT_UI }}>Completed: {fmtCheckTime(c.checked_at)}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right: Selfie thumbnails */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Camera size={13} className="text-[var(--accent)]" />
                  <span className="text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide" style={{ fontFamily: FONT_UI }}>
                    Selfies ({selfies.length})
                  </span>
                  {selfies.length > 0 && (
                    <button onClick={() => onOpenViewer(row, selfies)} className="ml-auto px-2 py-0.5 rounded-full bg-[rgba(59,130,246,0.12)] text-[#3b82f6] border border-[rgba(59,130,246,0.28)] text-[10px] font-extrabold" style={{ fontFamily: FONT_UI }}>
                      View All
                    </button>
                  )}
                </div>

                {selfies.length === 0 ? (
                  <div className="px-4 py-3 bg-[var(--card-bg)] rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] text-center" style={{ fontFamily: FONT_UI }}>
                    No selfies uploaded for this date
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selfies.map((s, i) => {
                      const isVerified = s.verify_status === "verified";
                      const isPending = s.verify_status === "pending";
                      return (
                        <div
                          key={s.selfie_pk}
                          onClick={() => onOpenViewer(row, selfies, i)}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border-2 flex-shrink-0 transition-transform hover:scale-105 group ${
                            isVerified ? "border-[#10b981]" : isPending ? "border-[#f59e0b]" : "border-[var(--surface-border)]"
                          }`}
                        >
                          {s.selfie_image_url ? (
                            <img
                              src={s.selfie_image_url}
                              alt={`selfie ${i + 1}`}
                              className="w-16 h-16 object-cover block"
                              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                            />
                          ) : null}
                          <div className="w-16 h-16 bg-[var(--surface)] items-center justify-center" style={{ display: s.selfie_image_url ? "none" : "flex" }}>
                            <Camera size={20} className="text-[var(--text-muted)]" />
                          </div>

                          <div className="absolute bottom-0.5 left-0.5 right-0.5 flex justify-between items-center">
  <span className="px-1 py-0.5 rounded bg-black/60 text-white text-[8px] font-bold">
    {s.selfie_type === "duty_start" ? "Login"
      : s.selfie_type === "duty_end" ? "Logout"
      : s.selfie_type === "campus" ? "Campus"
      : "Selfie"}
  </span>
  <div className={`w-2 h-2 rounded-full border border-white/50 ${isVerified ? "bg-emerald-500" : isPending ? "bg-amber-500" : "bg-red-500"}`} />
</div>

                          <div className="absolute inset-0 bg-black/35 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex">
                            <ZoomIn size={16} className="text-white" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────
// Selfie Viewer Modal
// ─────────────────────────────────────────────
const SelfieViewerModal = ({ row, selfies: initialSelfies, startIndex = 0, onClose }) => {
  const [selfies, setSelfies] = useState(initialSelfies || []);
  const [current, setCurrent] = useState(startIndex);
  const [loading, setLoading] = useState(!initialSelfies);

  useEffect(() => {
    if (initialSelfies) return;
    setLoading(true);
    const token = getToken();
    fetch(`${API}/sentinel-attendance/guard-detail?member_id=${row.member_id}&date=${row.att_date}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setSelfies(d.selfies || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [row.member_id, row.att_date, initialSelfies]);

  const selfie = selfies[current];
  const isVerified = selfie?.verify_status === "verified";
  const isPending = selfie?.verify_status === "pending";

  const prev = () => setCurrent(c => Math.max(0, c - 1));
  const next = () => setCurrent(c => Math.min(selfies.length - 1, c + 1));

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9000] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute top-0 left-0 right-0 px-6 py-4 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent">
        <div>
          <div className="text-white text-[13px] font-extrabold" style={{ fontFamily: FONT_UI }}>{row.guard_name}</div>
          <div className="text-white/60 text-[11px]" style={{ fontFamily: FONT_UI }}>{fmtDate(row.att_date)} · {row.customer_name}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-white/60 text-[13px]" style={{ fontFamily: FONT_UI }}>{current + 1} / {selfies.length}</span>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20">
            <X size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/60 text-[13px] flex items-center gap-3" style={{ fontFamily: FONT_UI }}>
          <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          Loading selfies…
        </div>
      ) : selfies.length === 0 ? (
        <div className="text-center text-white/50" style={{ fontFamily: FONT_UI }}>
          <Camera size={48} className="opacity-30 block mx-auto mb-4" />
          <p>No selfies found for this date</p>
        </div>
      ) : (
        <>
          <div className="relative flex items-center justify-center w-full" style={{ maxWidth: "min(600px, 90vw)", maxHeight: "60vh" }}>
            {current > 0 && (
              <button onClick={prev} className="absolute -left-14 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white z-10 hover:bg-white/20">
                <ChevronLeft size={20} />
              </button>
            )}

            {selfie?.selfie_image_url ? (
              <img
                src={selfie.selfie_image_url}
                alt={`Selfie ${current + 1}`}
                className={`max-w-full object-contain rounded-xl border-[3px] shadow-2xl ${
                  isVerified ? "border-emerald-500" : isPending ? "border-amber-500" : "border-red-500"
                }`}
                style={{ maxHeight: "60vh" }}
                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
              />
            ) : null}
            <div className="w-[300px] h-[300px] bg-white/5 rounded-xl border-2 border-dashed border-white/20 flex-col items-center justify-center gap-3" style={{ display: selfie?.selfie_image_url ? "none" : "flex" }}>
              <Camera size={40} className="text-white/30" />
              <span className="text-white/40 text-[13px]" style={{ fontFamily: FONT_UI }}>Image not available</span>
            </div>

            {current < selfies.length - 1 && (
              <button onClick={next} className="absolute -right-14 w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white z-10 hover:bg-white/20">
                <ChevronRight size={20} />
              </button>
            )}
          </div>

          <div className="mt-4 flex items-center gap-4 flex-wrap justify-center">
            <span
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-extrabold border ${
                isVerified
                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40"
                  : isPending
                  ? "bg-amber-500/20 text-amber-400 border-amber-500/40"
                  : "bg-red-500/20 text-red-400 border-red-500/40"
              }`}
              style={{ fontFamily: FONT_UI }}
            >
              {isVerified ? "✓ Verified" : isPending ? "⏳ Pending" : "✗ Unverified"}
            </span>
           <span className="text-white/60 text-[13px]" style={{ fontFamily: FONT_UI }}>
  {selfie?.selfie_type === "duty_start" ? "🔑 Login Selfie"
    : selfie?.selfie_type === "duty_end" ? "🚪 Logout Selfie"
    : selfie?.selfie_type === "campus" ? "📍 Campus Check Selfie"
    : "📷 Selfie"}
</span>
            {selfie?.created_at_utc && (
              <span className="text-white/50 font-mono text-[11px]">{fmtSelfieTime(selfie.created_at_utc)}</span>
            )}
            {selfie?.confidence_score && (
              <span className="text-white/50 text-[11px]" style={{ fontFamily: FONT_UI }}>
                Match: {(selfie.confidence_score * 100).toFixed(1)}%
              </span>
            )}
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto py-1" style={{ maxWidth: "min(600px, 90vw)" }}>
            {selfies.map((s, i) => {
              const isV = s.verify_status === "verified";
              const isP = s.verify_status === "pending";
              return (
                <div
                  key={s.selfie_pk}
                  onClick={() => setCurrent(i)}
                  className={`relative flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 cursor-pointer transition-opacity ${
                    i === current ? "border-white opacity-100" : isV ? "border-emerald-500 opacity-60" : isP ? "border-amber-500 opacity-60" : "border-white/20 opacity-60"
                  }`}
                >
                  {s.selfie_image_url ? (
                    <img src={s.selfie_image_url} alt={`thumb ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full bg-white/5 flex items-center justify-center">
                      <Camera size={14} className="text-white/30" />
                    </div>
                  )}
                  <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[8px] text-white/80 font-bold bg-black/50 px-1 rounded whitespace-nowrap">
  {s.selfie_type === "duty_start" ? "Login"
    : s.selfie_type === "duty_end" ? "Logout"
    : s.selfie_type === "campus" ? "Campus"
    : `#${i + 1}`}
</div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 text-white/30 text-[11px]" style={{ fontFamily: FONT_UI }}>
            ← → arrow keys to navigate · Esc to close
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Manual Override Modal
// ─────────────────────────────────────────────
const ManualOverrideModal = ({ row, onClose, onSave }) => {
  const calcAutoLateOt = () => {
    if (!row.login_time || !row.shift_mode) return { late: 0, ot: 0 };

    const shiftHours = parseInt(String(row.shift_mode).match(/(\d+)/)?.[1] || 8);
    const loginDt = new Date(row.login_time);
    const shiftStart = new Date(loginDt);
    shiftStart.setUTCHours(3, 30, 0, 0);
    const shiftEnd = new Date(shiftStart);
    shiftEnd.setTime(shiftEnd.getTime() + shiftHours * 60 * 60 * 1000);
    const logoutDt = new Date(loginDt);
    logoutDt.setTime(logoutDt.getTime() + shiftHours * 60 * 60 * 1000);
    const lateMins = (loginDt - shiftStart) / 60000;
    const late = lateMins > 15 ? parseFloat((lateMins / 60).toFixed(2)) : 0;
    const otMins = (logoutDt - shiftEnd) / 60000;
    const ot = otMins > 30 ? parseFloat((otMins / 60).toFixed(2)) : 0;
    return { late, ot };
  };

  const { late: autoLate, ot: autoOt } = calcAutoLateOt();

  const [attDesc, setAttDesc] = useState(row.attendance_desc || "FD");
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [lateHrs, setLateHrs] = useState(row.late_hrs || autoLate || "");
  const [otHrs, setOtHrs] = useState(row.ot_hrs || autoOt || "");
  const [saving, setSaving] = useState(false);
  const isCustom = reason === "Other (custom reason)";

  const handleSave = async () => {
    const finalReason = isCustom ? customReason : reason;
    if (!finalReason.trim()) { toast.error("Please select or enter a reason"); return; }
    setSaving(true);
    await onSave({ staff_id: row.staff_id, customer_id: row.customer_id, allocation_id: row.allocation_id, role_id: row.role_id, attendance_date: row.att_date, attendance_desc: attDesc, late_hrs: lateHrs || 0, ot_hrs: otHrs || 0, reason: finalReason });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[8888] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--card-bg)] rounded-2xl shadow-2xl w-full max-w-[520px] border border-[var(--card-border)] overflow-hidden">

        <div className="px-6 py-5 border-b border-[var(--divider)] flex items-center justify-between bg-[var(--surface)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[rgba(251,146,60,0.12)] flex items-center justify-center">
              <Edit2 size={16} className="text-[#fb923c]" />
            </div>
            <div>
              <div className="text-[var(--text-primary)] text-[13px] font-extrabold" style={{ fontFamily: FONT_HEADING }}>Manual Attendance Override</div>
              <div className="text-[var(--text-muted)] text-[11px]" style={{ fontFamily: FONT_UI }}>{row.guard_name} · {fmtDate(row.att_date)}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-3 bg-[var(--card-bg)] border-b border-[var(--divider)] flex gap-5 flex-wrap">
          {[
            { icon: <MapPin size={11} />, label: "Campus", value: row.customer_name },
            { icon: <Clock size={11} />, label: "Login", value: row.login_time_fmt },
            { icon: <Shield size={11} />, label: "Shift", value: row.shift_mode },
          ].map(({ icon, label, value }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span className="text-[var(--text-muted)]">{icon}</span>
              <span className="text-[var(--text-muted)] text-[11px]" style={{ fontFamily: FONT_UI }}>{label}:</span>
              <span className="text-[var(--text-primary)] text-[11px] font-extrabold" style={{ fontFamily: FONT_UI }}>{value || "—"}</span>
            </div>
          ))}
        </div>

        <div className="px-6 py-5 flex flex-col gap-4 max-h-[55vh] overflow-y-auto">
          <div>
            <label className="block text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-2" style={{ fontFamily: FONT_UI }}>Mark As *</label>
            <div className="flex gap-1.5 flex-wrap">
              {ATT_OPTIONS.map((opt) => {
                const c = ATT_CONFIG[opt.value];
                const sel = attDesc === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setAttDesc(opt.value)}
                    className={`px-3.5 py-2 rounded-xl border-2 text-[13px] font-extrabold transition-colors ${sel ? c.selected : "bg-[var(--card-bg)] border-[var(--surface-border)] text-[var(--text-muted)]"}`}
                  >
                    {opt.value}
                  </button>
                );
              })}
            </div>
            {attDesc && <div className="mt-2"><AttBadge desc={attDesc} /></div>}
          </div>

          <div>
            <label className="block text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-2" style={{ fontFamily: FONT_UI }}>Reason *</label>
            <div className="flex flex-col gap-1">
              {REASON_PRESETS.map((r) => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  className={`px-3.5 py-2 text-left rounded-xl border-[1.5px] text-[13px] flex items-center gap-2.5 transition-colors ${
                    reason === r ? "bg-[rgba(59,130,246,0.12)] border-[#3b82f6] text-[#3b82f6] font-extrabold" : "bg-[var(--card-bg)] border-[var(--surface-border)] text-[var(--text-primary)]"
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${reason === r ? "border-[#3b82f6] bg-[#3b82f6]" : "border-[var(--surface-border)] bg-transparent"}`}>
                    {reason === r && <Check size={8} className="text-white" />}
                  </span>
                  {r}
                </button>
              ))}
            </div>
            {isCustom && (
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter custom reason..."
                rows={3}
                className="mt-2 w-full px-3.5 py-2.5 bg-[var(--card-bg)] border-[1.5px] border-[var(--surface-border)] rounded-xl text-[var(--text-primary)] text-[13px] outline-none focus:border-[#3b82f6] resize-y"
                style={{ fontFamily: FONT_UI }}
              />
            )}
          </div>

          {row.login_time && (
            <div className="px-4 py-3 bg-[var(--surface)] rounded-xl border border-[var(--surface-border)]">
              <div className="text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-2.5" style={{ fontFamily: FONT_UI }}>
                Time Calculation
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "Shift Start", value: (() => { const d = new Date(row.login_time); d.setUTCHours(3, 30, 0, 0); return fmtTime(d); })(), cls: "text-[var(--text-muted)]" },
                  { label: "Login Time", value: row.login_time_fmt, cls: autoLate > 0 ? "text-[#ef4444]" : "text-[#10b981]" },
                  { label: "Shift End", value: (() => { const shiftHrs = parseInt(String(row.shift_mode).match(/(\d+)/)?.[1] || 8); const d = new Date(row.login_time); d.setUTCHours(3, 30, 0, 0); return fmtTime(new Date(d.getTime() + shiftHrs * 3600000)); })(), cls: "text-[var(--text-muted)]" },
                  { label: "Logout", value: row.logout_time_fmt, cls: "text-[#6366f1]" },
                  { label: "Late", value: autoLate > 0 ? `${autoLate}h` : "On time", cls: autoLate > 0 ? "text-[#f59e0b]" : "text-[#10b981]" },
                  { label: "OT", value: autoOt > 0 ? `${autoOt}h` : "None", cls: autoOt > 0 ? "text-[#8b5cf6]" : "text-[var(--text-muted)]" },
                ].map(({ label, value, cls }) => (
                  <div key={label} className="text-center">
                    <div className="text-[var(--text-muted)] text-[10px] mb-0.5" style={{ fontFamily: FONT_UI }}>{label}</div>
                    <div className={`font-mono text-[13px] font-extrabold ${cls}`}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {[
              { label: "Late Hours (editable)", value: lateHrs, set: setLateHrs, cls: "text-[#f59e0b] border-[rgba(245,158,11,0.28)]", hintCls: "text-[#f59e0b]", hint: autoLate > 0 ? `Auto: ${autoLate}h` : "On time" },
              { label: "OT Hours (editable)", value: otHrs, set: setOtHrs, cls: "text-[#8b5cf6] border-[rgba(139,92,246,0.28)]", hintCls: "text-[#8b5cf6]", hint: autoOt > 0 ? `Auto: ${autoOt}h` : "None" },
            ].map(({ label, value, set, cls, hintCls, hint }) => (
              <div key={label} className="flex-1">
                <label className="flex justify-between items-center text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-1.5" style={{ fontFamily: FONT_UI }}>
                  <span>{label.split(" ")[0]} {label.split(" ")[1]}</span>
                  <span className={`text-[10px] font-extrabold normal-case tracking-normal ${hintCls}`}>{hint}</span>
                </label>
                <input
                  type="number" min="0" max="24" step="0.25"
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  className={`w-full px-3 py-2 bg-[var(--card-bg)] border-[1.5px] rounded-xl font-mono text-[14px] font-extrabold text-center outline-none ${cls}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[var(--divider)] flex gap-3 justify-end bg-[var(--surface)]">
          <button onClick={onClose} className="px-5 py-2.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[13px] font-medium hover:bg-[var(--surface)]">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-2.5 bg-[var(--accent)] rounded-xl text-white text-[13px] font-extrabold flex items-center gap-2 ${
              saving ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"
            }`}
          >
            {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving…</> : <><Check size={14} /> Save Override</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════
// MAIN DASHBOARD
// ════════════════════════════════════════════════════
export default function SentinelAttendanceDashboard() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [companyId, setCompanyId] = useState("");
  const [allocationId, setAllocationId] = useState("");
  const [attStatus, setAttStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateView, setDateView] = useState("today");
  const [page, setPage] = useState(1);
  const limit = 10;

  const [companies, setCompanies] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [activeAlloc, setActiveAlloc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAlloc, setLoadingAlloc] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [overrideRow, setOverrideRow] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [viewerData, setViewerData] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const menuRef = useRef(null);

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    loadCompanies(token);
  }, []);

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const companyDetails = data?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const showToast = (type, message) => {
    toast[type](message);
  };

  const totalPages = Math.ceil(total / limit);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const loadCompanies = async (token) => {
    try {
      const res = await fetch(`${API}/sentinel-attendance/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (d.success) setCompanies(d.data);
    } catch (error) {
      console.error("Load companies error:", error);
    }
  };

  useEffect(() => {
    if (!companyId) {
      setAllocations([]);
      setAllocationId("");
      setActiveAlloc(null);
      setRows([]);
      setSummary({});
      return;
    }
    setLoadingAlloc(true);
    setAllocationId("");
    setActiveAlloc(null);
    setRows([]);
    setSummary({});
    const token = getToken();
    fetch(`${API}/sentinel-attendance/allocations?customer_id=${companyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setAllocations(d.data);
          if (d.data.length) {
            setAllocationId(String(d.data[0].id));
            setActiveAlloc(d.data[0]);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoadingAlloc(false));
  }, [companyId]);

  const loadData = useCallback(async () => {
    if (!allocationId) return;
    setLoading(true);
    try {
      const token = getToken();
      const todayStr = getToday();
      const params = new URLSearchParams({
        allocation_id: allocationId,
        page,
        limit,
        ...(attStatus && { attendance_status: attStatus }),
        ...(search && { search }),
        ...(dateView === "today" && { from_date: todayStr, to_date: todayStr }),
      });
      const res = await fetch(`${API}/sentinel-attendance/dashboard?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setRows(data.data);
        setSummary(data.summary || {});
        setTotal(data.total || 0);
      } else {
        showToast("error", data.message || "Failed to load");
        setRows([]);
        setSummary({});
        setTotal(0);
      }
    } catch {
      showToast("error", "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [allocationId, attStatus, search, page, limit, dateView]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
  }, [allocationId, attStatus, search, dateView]);

  const handlePushAuto = async () => {
    if (!allocationId) {
      showToast("error", "Select an allocation first");
      return;
    }
    setPushing(true);
    try {
      const token = getToken();
      const res = await fetch(`${API}/sentinel-attendance/push-auto`, {
        method: "POST",
        body: JSON.stringify({ allocation_id: allocationId }),
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        showToast("success", data.message);
        loadData();
      } else showToast("error", data.message);
    } catch {
      showToast("error", "Push failed");
    } finally {
      setPushing(false);
    }
  };

  const handleOverrideSave = async (payload) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/sentinel-attendance/manual-override`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        showToast("success", "Attendance overridden!");
        setOverrideRow(null);
        loadData();
      } else showToast("error", data.message);
    } catch {
      showToast("error", "Override failed");
    }
  };

  const toggleExpand = (row) => {
    const key = `${row.staff_id}_${row.att_date}`;
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  const openViewer = (row, selfies = null, startIndex = 0) => {
    setViewerData({ row, selfies, startIndex });
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const COL_COUNT = 12;

  return (
    <Layout dashboardData={dashboardData} routePage={routePage} activePage="sentinel-attendance">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        {overrideRow && <ManualOverrideModal row={overrideRow} onClose={() => setOverrideRow(null)} onSave={handleOverrideSave} />}
        {viewerData && <SelfieViewerModal row={viewerData.row} selfies={viewerData.selfies} startIndex={viewerData.startIndex || 0} onClose={() => setViewerData(null)} />}

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[var(--accent-faint)] flex items-center justify-center">
              <Shield size={22} className="text-[var(--accent)]" />
            </div>
            <div>
              <h1 className="text-[var(--text-primary)] text-[22px] font-extrabold m-0" style={{ fontFamily: FONT_HEADING }}>
                Sentinel Attendance &amp; Payroll
              </h1>
              <p className="text-[var(--text-muted)] text-[13px] m-0 mt-0.5" style={{ fontFamily: FONT_UI }}>
                {activeAlloc ? (
                  <>
                    <span className="text-[var(--accent)] font-extrabold">{activeAlloc.customer_name}</span> · {activeAlloc.label} · {activeAlloc.shift_mode}
                  </>
                ) : (
                  "Select a company and allocation period to begin"
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2.5 flex-wrap">
            <button
              onClick={handlePushAuto}
              disabled={pushing || !allocationId}
              className={`flex items-center gap-2 px-4 py-2.5 bg-[rgba(99,102,241,0.12)] border-[1.5px] border-[rgba(99,102,241,0.28)] rounded-xl text-[#6366f1] text-[13px] font-extrabold ${
                pushing || !allocationId ? "opacity-60 cursor-not-allowed" : "hover:bg-[rgba(99,102,241,0.2)]"
              }`}
            >
              {pushing ? <RefreshCw size={14} className="animate-spin" /> : <TrendingUp size={14} />}
              {pushing ? "Pushing…" : "Push Today's Auto"}
            </button>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[13px] hover:bg-[var(--surface)]"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 bg-[var(--btn-bg)] rounded-xl text-[var(--btn-text)] text-[13px] font-extrabold hover:opacity-90">
              <Download size={14} /> Export
            </button>
          </div>
        </div>

        {/* ── Summary cards ── */}
        {activeAlloc && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[
              { label: "Total Guards", value: summary.total_employees || 0, icon: <Users size={18} />, cls: "text-[#3b82f6]", bg: "bg-[rgba(59,130,246,0.12)]", sub: "In allocation" },
              { label: "Present", value: summary.present || 0, icon: <CheckCircle size={18} />, cls: "text-[#10b981]", bg: "bg-[rgba(16,185,129,0.12)]", sub: summary.total_employees ? `${Math.round(((summary.present || 0) / summary.total_employees) * 100)}% of total` : "—" },
              { label: "Absent", value: summary.absent || 0, icon: <XCircle size={18} />, cls: "text-[#ef4444]", bg: "bg-[rgba(239,68,68,0.12)]", sub: summary.total_employees ? `${Math.round(((summary.absent || 0) / summary.total_employees) * 100)}% of total` : "—" },
              { label: "Half Day", value: summary.half_day || 0, icon: <Clock size={18} />, cls: "text-[#f59e0b]", bg: "bg-[rgba(245,158,11,0.12)]", sub: "HD / HN" },
              { label: "Total Payroll Hrs", value: `${(summary.total_payroll_hours || 0).toFixed(1)}h`, icon: <IndianRupee size={18} />, cls: "text-[#8b5cf6]", bg: "bg-[rgba(139,92,246,0.12)]", sub: "Estimated hours" },
            ].map((s) => (
              <div
                key={s.label}
                className={`${s.bg} border border-[var(--surface-border)] rounded-2xl px-4 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className={`font-mono text-[26px] font-extrabold leading-none ${s.cls}`}>{s.value}</div>
                    <div className="text-[var(--text-muted)] text-[10px] mt-1.5 font-extrabold uppercase tracking-wide">{s.label}</div>
                    <div className={`text-[10px] mt-1 opacity-70 ${s.cls}`}>{s.sub}</div>
                  </div>
                  <div className={`${s.cls} opacity-35`}>{s.icon}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Filters ── */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-4">
          <div className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-1.5">
                1. Select Company *
              </label>
              <select
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] cursor-pointer"
              >
                <option value="">-- Select Company --</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.customer_name}
                    {c.town_city ? ` — ${c.town_city}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[150px]">
              <label className="block text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-1.5">
                Status
              </label>
              <select
                value={attStatus}
                onChange={(e) => {
                  setAttStatus(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="half">Half Day</option>
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-1.5">
                Search
              </label>
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
                <input
                  type="text"
                  placeholder="Name or Employee ID..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] pl-8 placeholder:text-[var(--text-faint)]"
                />
              </div>
            </div>

            {/* Date view tabs */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide">
                Date
              </label>
              <div className="flex bg-[var(--surface)] border-[1.5px] border-[var(--surface-border)] rounded-xl overflow-hidden">
                {[
                  { id: "today", label: "📅 Today" },
                  { id: "past", label: "🗓 All Days" },
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => {
                      setDateView(v.id);
                      setPage(1);
                    }}
                    className={`px-3.5 py-2 text-[13px] whitespace-nowrap ${
                      dateView === v.id
                        ? "bg-[var(--btn-bg)] text-[var(--btn-text)] font-extrabold"
                        : "text-[var(--text-muted)] font-medium"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Allocation tabs ── */}
        {companyId && (
          <div className="mb-5">
            <div className="text-[var(--text-muted)] text-[11px] font-extrabold uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Calendar size={12} /> 2. Select Allocation Period
            </div>
            {loadingAlloc ? (
              <div className="flex items-center gap-2 text-[var(--text-muted)] text-[13px]">
                <div className="w-3.5 h-3.5 border-2 border-[var(--surface-border)] border-t-[var(--accent)] rounded-full animate-spin" />
                Loading allocations...
              </div>
            ) : allocations.length === 0 ? (
              <div className="p-4 bg-[var(--card-bg)] border border-dashed border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[13px] text-center">
                No allocations found for this company
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                {allocations.map((alloc) => {
                  const isActive = String(allocationId) === String(alloc.id);
                  return (
                    <button
                      key={alloc.id}
                      onClick={() => {
                        setAllocationId(String(alloc.id));
                        setActiveAlloc(alloc);
                        setPage(1);
                        setExpandedKey(null);
                      }}
                      className={`px-4 py-2.5 rounded-xl border-[1.5px] text-left flex flex-col items-start gap-0.5 transition-colors ${
                        isActive
                          ? "bg-[var(--btn-bg)] border-[var(--btn-bg)]"
                          : "bg-[var(--card-bg)] border-[var(--surface-border)] hover:border-[var(--accent-border)]"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wide ${
                            isActive
                              ? "bg-white/20 text-white"
                              : alloc.allocation_type === "period"
                              ? "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]"
                              : "bg-[rgba(99,102,241,0.12)] text-[#6366f1]"
                          }`}
                        >
                          {alloc.allocation_type === "period" ? "Period" : "Month"}
                        </span>
                        {alloc.emp_count > 0 && (
                          <span
                            className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold ${
                              isActive
                                ? "bg-white/15 text-white/85"
                                : "bg-[var(--surface)] text-[var(--text-muted)]"
                            }`}
                          >
                            {alloc.emp_count} staff
                          </span>
                        )}
                      </div>
                      <span className={`text-[13px] font-extrabold ${isActive ? "text-white" : "text-[var(--text-primary)]"}`}>
                        {alloc.label}
                      </span>
                      <span className={`text-[10px] ${isActive ? "text-white/70" : "text-[var(--text-muted)]"}`}>
                        {alloc.shift_mode}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!companyId && (
          <div className="text-center py-16 px-8 bg-[var(--card-bg)] border-2 border-dashed border-[var(--surface-border)] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <Shield size={48} className="opacity-10 block mx-auto mb-4" />
            <p className="text-[var(--text-muted)] text-[15px] font-extrabold m-0">Select a company to view attendance</p>
            <p className="text-[var(--text-muted)] text-[13px] mt-1.5 m-0">Then pick an allocation period</p>
          </div>
        )}

        {/* ── Table ── */}
        {allocationId && (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            {rows.length > 0 && !loading && (
              <div className="px-4 py-2 bg-[var(--accent-faint)] border-b border-[var(--divider)] flex items-center gap-2">
                <ChevronDown size={12} className="text-[var(--text-muted)]" />
                <span className="text-[var(--text-muted)] text-[11px]">Click any row to expand campus checks &amp; selfies</span>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] border-collapse">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    {[
                      { label: "", w: "36px" },
                      { label: "Employee Details", w: "190px" },
                      { label: "Site / Campus", w: "140px" },
                      { label: "Shift", w: "140px" },
                      { label: "Login Time", w: "95px" },
                      { label: "Logout Time", w: "95px" },
                      { label: "Selfie & Campus", w: "140px" },
                      { label: "Attendance Status", w: "130px" },
                      { label: "Source", w: "85px" },
                      { label: "Payroll Hours", w: "95px" },
                      { label: "Reason", w: "160px" },
                      { label: "Actions", w: "70px" },
                    ].map(({ label, w }) => (
                      <th
                        key={label}
                        className="px-3 py-3 text-left text-white text-[11px] font-extrabold uppercase tracking-wide whitespace-nowrap border-b border-white/10"
                        style={{ minWidth: w }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={COL_COUNT} className="p-12 text-center text-[var(--text-muted)]">
                        <div className="inline-flex items-center gap-3">
                          <div className="w-[18px] h-[18px] border-2 border-[var(--surface-border)] border-t-[var(--accent)] rounded-full animate-spin" />
                          Loading attendance data…
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={COL_COUNT} className="p-12 text-center">
                        <Users size={40} className="opacity-10 block mx-auto mb-3" />
                        <p className="text-[var(--text-muted)] text-[13px] font-extrabold m-0">No records found</p>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    rows.map((row, idx) => {
                      const rowKey = `${row.staff_id}_${row.att_date}`;
                      const isExpanded = expandedKey === rowKey;
                      const isEven = idx % 2 === 0;
                      const isFuture = row.att_date > getToday();

                      return (
                        <React.Fragment key={rowKey}>
                          {/* ── Main row ── */}
                          <tr
                            onClick={() => !isFuture && toggleExpand(row)}
                            className={`${
                              isFuture
                                ? "opacity-45 cursor-not-allowed bg-[repeating-linear-gradient(45deg,var(--surface),var(--surface)_3px,var(--surface-border)_3px,var(--surface-border)_5px)]"
                                : "cursor-pointer hover:bg-[var(--accent-faint)]"
                            } ${
                              isExpanded
                                ? "border-b-0 bg-[var(--accent-faint)]"
                                : `border-b border-[var(--divider)] ${isEven ? "" : "bg-[var(--surface)]"}`
                            }`}
                          >
                            {/* Expand toggle */}
                            <td className="pl-3 pr-2 py-3 text-center">
                              <div className={`flex items-center justify-center ${isExpanded ? "text-[var(--accent)]" : "text-[var(--text-muted)]"}`}>
                                {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                              </div>
                            </td>

                            {/* Employee */}
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2.5">
                                <div className="w-[34px] h-[34px] rounded-full bg-[var(--accent-faint)] flex items-center justify-center flex-shrink-0 border-2 border-[var(--surface-border)] overflow-hidden">
                                  {row.selfie_url ? (
                                    <img src={row.selfie_url} alt="guard" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                                  ) : (
                                    <Shield size={13} className="text-[var(--accent)]" />
                                  )}
                                </div>
                                <div>
                                  <div className="text-[var(--text-primary)] text-[13px] font-extrabold whitespace-nowrap">{row.guard_name}</div>
                                  <div className="text-[var(--accent)] font-mono text-[10px] font-extrabold">{row.application_number}</div>
                                  {row.role_name && <div className="text-[var(--text-muted)] text-[9px]">{row.role_name}</div>}
                                </div>
                              </div>
                            </td>

                            {/* Campus */}
                            <td className="px-3 py-3">
                              <div className="text-[var(--text-primary)] text-[13px] font-extrabold whitespace-nowrap">{row.customer_name}</div>
                              <div className="text-[var(--text-muted)] text-[10px] flex items-center gap-1 mt-0.5">
                                <MapPin size={9} /> {row.campus_name}
                              </div>
                            </td>

                            {/* Shift */}
                            <td className="px-3 py-3">
                              <div className="text-[var(--text-primary)] text-[13px] font-extrabold">{row.shift_mode}</div>
                              <div className="text-[var(--text-muted)] text-[10px]">{row.shift_start_fmt} – {row.shift_end_fmt}</div>
                              <div className="text-[var(--text-muted)] text-[9px] mt-0.5">{fmtDate(row.att_date)}</div>
                            </td>

                            {/* Login */}
                            <td className="px-3 py-3">
                              <div className={`font-mono text-[13px] font-extrabold ${row.login_time ? "text-[#10b981]" : "text-[var(--text-muted)]"}`}>
                                {row.login_time_fmt}
                              </div>
                            </td>

                            {/* Logout */}
                            <td className="px-3 py-3">
                              <div className={`font-mono text-[13px] font-extrabold ${row.logout_time ? "text-[#6366f1]" : "text-[var(--text-muted)]"}`}>
                                {row.logout_time_fmt}
                              </div>
                            </td>

                            {/* Selfie & Campus count summary */}
                            <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                              <SelfieCountCell row={row} onOpenViewer={openViewer} />
                            </td>

                            {/* Attendance */}
                            <td className="px-3 py-3">
                              <AttBadge desc={row.attendance_desc} />
                            </td>

                            {/* Source */}
                            <td className="px-3 py-3">
                              <SourceBadge source={row.att_source} />
                            </td>

                            {/* Payroll hours */}
                            <td className="px-3 py-3">
                              <div className={`font-mono text-[14px] font-extrabold ${row.payroll_hours > 0 ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"}`}>
                                {row.payroll_hours_fmt}
                              </div>
                              {row.ot_hrs > 0 && <div className="text-[#8b5cf6] text-[9px] font-extrabold">OT: {row.ot_hrs}h</div>}
                            </td>

                            {/* Reason */}
                            <td className="px-3 py-3 max-w-[160px]">
                              {row.reason ? (
                                <div className="flex flex-col gap-1">
                                  <span className="px-1.5 py-0.5 rounded bg-[rgba(251,146,60,0.12)] text-[#fb923c] text-[9px] font-extrabold w-fit">✏ Manual</span>
                                  <span className="text-[var(--text-muted)] text-[11px] leading-snug line-clamp-2" title={row.reason}>
                                    {row.reason}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[var(--text-muted)] text-[13px]">—</span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="relative" ref={openMenu === rowKey ? menuRef : null}>
                                <button
                                  onClick={() => setOpenMenu(openMenu === rowKey ? null : rowKey)}
                                  className="p-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-md text-[var(--text-muted)] hover:bg-[var(--surface)]"
                                >
                                  <MoreVertical size={15} />
                                </button>
                                {openMenu === rowKey && (
                                  <div className="absolute right-0 top-[calc(100%+4px)] z-50 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl shadow-lg min-w-[175px] overflow-hidden">
                                    {[
                                      { icon: <Camera size={13} />, label: "View Selfies", action: () => { openViewer(row); setOpenMenu(null); } },
                                      { icon: <Edit2 size={13} />, label: "Manual Override", action: () => { setOverrideRow(row); setOpenMenu(null); } },
                                    ].map(({ icon, label, action }) => (
                                      <button
                                        key={label}
                                        onClick={action}
                                        className="flex items-center gap-2.5 px-4 py-2.5 border-b border-[var(--divider)] last:border-b-0 w-full text-left text-[var(--text-primary)] text-[13px] hover:bg-[var(--surface)]"
                                      >
                                        <span className="text-[var(--text-muted)]">{icon}</span> {label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* ── Expanded row ── */}
                          {!isFuture && isExpanded && (
                            <ExpandedRow
                              key={`${rowKey}_expanded`}
                              row={row}
                              colSpan={COL_COUNT}
                              onOpenViewer={openViewer}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="px-5 py-3.5 border-t border-[var(--divider)] flex items-center justify-between flex-wrap gap-3 bg-[var(--surface)]">
                <div className="text-[var(--text-muted)] text-[13px]">
                  Showing <strong className="text-[var(--text-primary)]">{((page - 1) * limit) + 1}</strong> to{" "}
                  <strong className="text-[var(--text-primary)]">{Math.min(page * limit, total)}</strong> of{" "}
                  <strong className="text-[var(--text-primary)]">{total}</strong> entries
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page <= 1}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[11px] ${
                      page <= 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    «
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] flex items-center ${
                      page <= 1 ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pg = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return pg <= totalPages ? (
                      <button
                        key={pg}
                        onClick={() => setPage(pg)}
                        className={`min-w-9 px-2.5 py-1.5 rounded-xl border font-mono text-[11px] font-extrabold ${
                          page === pg
                            ? "bg-[var(--btn-bg)] border-[var(--btn-bg)] text-[var(--btn-text)]"
                            : "bg-[var(--card-bg)] border-[var(--surface-border)] text-[var(--text-muted)] hover:bg-[var(--surface)]"
                        }`}
                      >
                        {pg}
                      </button>
                    ) : null;
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] flex items-center ${
                      page >= totalPages ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    <ChevronRight size={14} />
                  </button>
                  <button
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    className={`px-2.5 py-1.5 bg-[var(--card-bg)] border border-[var(--surface-border)] rounded-xl text-[var(--text-muted)] text-[11px] ${
                      page >= totalPages ? "opacity-40 cursor-not-allowed" : "hover:bg-[var(--surface)]"
                    }`}
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}